// world.js — 歩き回れる場所。ホール・店の外・自宅を行き来する。
//
// タイルマップ + BFS の経路探索で構成している。画像は使わず、床も建物も
// キャラクターもすべてコードで描く（この作品の他の部分と同じ方針）。
//
// 場所ごとの中身は areas.js が持ち、ここはその歩きかたと画面だけを扱う。

import {
  MACHINES, estimatePayout, nailLabel,
  machineName, machineShort, machineType, machineBlurb,
} from './machines.js';
import { NPCS, npcName, todayKey } from './npcs.js';
import { CUSTOM_SLOTS } from './custom.js';
import { AREAS, TILE } from './areas.js';
import {
  PRIZES, PRIZE_GROUPS, RARITY, BALLS_PER_ING,
  rateOf, collected, totalOwned, prizeName, prizeBlurb, groupLabel,
} from './prizes.js';
import { t, tList, num } from './i18n.js';

export { TILE };

const DIRS = [[0, 1], [-1, 0], [1, 0], [0, -1]];   // down, left, right, up
// DOWN が 0 なので、向きの判定は必ず !== null / === DOWN で行うこと。
// truthy 判定にすると下方向だけ黙って効かなくなる。
const DOWN = 0, LEFT = 1, RIGHT = 2, UP = 3;

const PLAYER_R = 8;
const SPEED = 1.75;

// 記号が二役を持つと、片方が黙ってもう片方に化ける（入口マットの E を自作台の
// 枠に使ってしまい、入口が通れなくなった実例がある）。起動時に見ておく。
for (const area of Object.values(AREAS)) {
  const roles = [
    area.machines || '', area.customs || '', area.npcs || '',
    Object.keys(area.spots || {}).join(''),
    Object.keys(area.doors || {}).join(''),
    Object.keys(area.scenery || {}).join(''),
  ].join('');
  const dup = [...roles].filter((ch, i) => roles.indexOf(ch) !== i);
  if (dup.length) console.warn(`${area.id}: 記号が重複しています: ${[...new Set(dup)].join(' ')}`);
  if ((area.customs || '').length && area.customs.length !== CUSTOM_SLOTS) {
    console.warn(`${area.id}: 自作台の枠が ${area.customs.length} 個（${CUSTOM_SLOTS} 個必要）`);
  }
  for (const row of area.map) {
    if (row.length !== area.map[0].length) console.warn(`${area.id}: マップの行の長さが揃っていません`);
  }
}
if (NPCS.length !== (AREAS.hall.npcs || '').length) {
  console.warn(`NPC の数(${NPCS.length})とマップの記号(${AREAS.hall.npcs.length})が一致しません`);
}
{
  const placed = AREAS.hall.map.join('').split('').filter((ch) => AREAS.hall.machines.includes(ch));
  if (new Set(placed).size !== MACHINES.length) {
    console.warn(`機種 ${MACHINES.length} 台に対し、マップに置かれているのは ${new Set(placed).size} 台です`);
  }
}

export class World {
  /**
   * @param {HTMLElement} root  画面のルート
   * @param {object} handlers
   *   onPick(machine)        台が決定されたとき
   *   onEdit(machine, slot)  自作台を編集/新規作成するとき（machine は無ければ null）
   *   onDelete(machine)      自作台を捨てるとき
   *   onTrade(kind, payload) 交換・換金・玉貸し。{ok, msg} を返す
   * @param {object} [audio]    効果音（任意）
   */
  constructor(root, handlers, audio) {
    this.root = root;
    this.on = handlers || {};
    this.audio = audio;
    this.stats = {};
    this.wallets = { ball: 0, medal: 0, ing: 0 };
    this.customs = [];         // 自作台（slot 番目に入る。無ければ undefined）
    this.collection = {};      // 景品の所持数 {prizeId: n}
    this.frame = 0;
    this.visible = false;

    this.p = { x: 0, y: 0, dir: UP, moving: false, anim: 0 };
    this.path = null;          // 自動歩行の経路
    this.pathGoal = null;      // 到着後に開くもの
    this.keys = new Set();
    this.panelFor = null;      // 情報パネルに出しているもの {kind, machine, slot}
    this.shop = null;          // 開いている売店 {kind, tab, msg}
    this.talk = null;          // 会話中
    // 案内係。他の NPC と違ってタイルに固定されておらず、一緒に歩く
    this.guide = null;

    this._build();
    this.goTo('hall');
  }

  // ---------------------------------------------------------------- 構築

  _build() {
    this.root.innerHTML = '';

    const head = document.createElement('div');
    head.className = 'hall-head';
    head.innerHTML = `
      <h1 class="hall-title"></h1>
      <p class="hall-sub"></p>
      <div class="hall-wallets">
        <div class="hall-wallet"><b data-w="ball">0</b><span>${esc(t('currency.ball.label'))}</span></div>
        <div class="hall-wallet medal"><b data-w="medal">0</b><span>${esc(t('currency.medal.label'))}</span></div>
        <div class="hall-wallet ing"><b data-w="ing">0</b><span>${esc(t('currency.ing.label'))}</span></div>
      </div>`;
    this.titleEl = head.querySelector('.hall-title');
    this.subEl = head.querySelector('.hall-sub');
    this.walletEls = {
      ball: head.querySelector('[data-w="ball"]'),
      medal: head.querySelector('[data-w="medal"]'),
      ing: head.querySelector('[data-w="ing"]'),
    };
    this.root.append(head);

    const stage = document.createElement('div');
    stage.className = 'hall-stage';
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hall-canvas';
    this.canvas.setAttribute('aria-label', t('world.mapAria'));
    stage.append(this.canvas);
    this.root.append(stage);
    this.ctx = this.canvas.getContext('2d');

    this.noteEl = document.createElement('p');
    this.noteEl.className = 'hall-note';
    this.root.append(this.noteEl);

    // 会話ウィンドウ
    this.talkBox = document.createElement('div');
    this.talkBox.className = 'talkbox gone';
    this.talkBox.innerHTML = `
      <div class="talkbox-inner">
        <span class="talk-name"></span>
        <p class="talk-line"></p>
        <div class="talk-choices gone"></div>
        <span class="talk-next">▼</span>
      </div>`;
    this.talkName = this.talkBox.querySelector('.talk-name');
    this.talkLine = this.talkBox.querySelector('.talk-line');
    this.choiceBox = this.talkBox.querySelector('.talk-choices');
    this.talkBox.addEventListener('pointerdown', (e) => { e.stopPropagation(); this._advance(); });
    this.root.append(this.talkBox);

    // 台の情報パネル
    this.panel = document.createElement('div');
    this.panel.className = 'mpanel gone';
    this.root.append(this.panel);

    // 売店（景品交換・換金・コレクション）
    this.shopEl = document.createElement('div');
    this.shopEl.className = 'shop gone';
    this.root.append(this.shopEl);

    this.canvas.addEventListener('pointerdown', (e) => this._onPointer(e));
    window.addEventListener('resize', () => this._resize());
    this._resize();
  }

  _resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const r = this.canvas.getBoundingClientRect();
    const w = r.width || this.mapW, h = r.height || this.mapH;
    this.canvas.width = Math.max(1, Math.round(w * dpr));
    this.canvas.height = Math.max(1, Math.round(h * dpr));
    this.scale = Math.min(w / this.mapW, h / this.mapH) * dpr;
    this.offX = (this.canvas.width - this.mapW * this.scale) / 2;
    this.offY = (this.canvas.height - this.mapH * this.scale) / 2;
  }

  // ---------------------------------------------------------------- 場所

  /** 別の場所へ移る。spawn はタイル座標 */
  goTo(areaId, spawn) {
    const area = AREAS[areaId] || AREAS.hall;
    this.area = area;
    this.cols = area.map[0].length;
    this.rows = area.map.length;
    this.mapW = this.cols * TILE;
    this.mapH = this.rows * TILE;

    // 棚のタイルは左上から順に景品を載せていくので、並び順を先に出しておく
    this.shelfTiles = [];
    for (let rr = 0; rr < this.rows; rr++) {
      for (let cc = 0; cc < this.cols; cc++) {
        if (area.spots?.[this._at(cc, rr)]?.shelf) this.shelfTiles.push([cc, rr]);
      }
    }

    // 案内係はホールにだけいる。持ち場に立たせておく
    this.guide = area.guide ? {
      x: area.guide.home[0] * TILE + TILE / 2,
      y: area.guide.home[1] * TILE + TILE / 2,
      dir: DOWN, path: null, onArrive: null, moving: false, anim: 0,
      body: area.guide.body, color: area.guide.color, home: area.guide.home,
    } : null;

    const [c, r] = spawn || area.spawn;
    this.p.x = c * TILE + TILE / 2;
    this.p.y = r * TILE + TILE / 2;
    this.p.dir = UP;
    this.p.moving = false;
    this.path = null;
    this.pathGoal = null;
    this.keys.clear();
    this._closePanel();
    this._closeShop();
    this._closeDialogue();

    this.titleEl.textContent = t(`area.${area.id}.title`);
    this.subEl.textContent = t(`area.${area.id}.sub`);
    this.noteEl.textContent = t(`area.${area.id}.note`);
    // 場所ごとに画面全体の色を変える。ホールは赤、外は夜の青、家は暖色。
    this.root.style.background = area.background;
    this.canvas.style.aspectRatio = `${this.mapW} / ${this.mapH}`;
    this._resize();
  }

  _at(c, r) {
    if (r < 0 || r >= this.rows || c < 0 || c >= this.cols) return '#';
    return this.area.map[r][c];
  }

  _solid(c, r) { return this.area.solid.includes(this._at(c, r)); }

  _isMachineTile(c, r) {
    const ch = this._at(c, r);
    return (this.area.machines || '').includes(ch) || (this.area.customs || '').includes(ch);
  }

  // ---------------------------------------------------------------- 入力

  onKeyDown(e) {
    if (!this.visible) return false;
    if (this.talk) {
      if (e.key === 'Escape') { this._closeDialogue(); return true; }
      if (e.key === 'ArrowLeft' && this._movePick(-1)) return true;
      if (e.key === 'ArrowRight' && this._movePick(1)) return true;
      if (e.code === 'Space' || e.key === 'Enter') { this._advance(); return true; }
      return true;
    }
    if (this.shop) {
      if (e.key === 'Escape') { this._closeShop(); return true; }
      return true;
    }
    if (this.panelFor) {
      if (e.key === 'Escape') { this._closePanel(); return true; }
      if (e.code === 'Space' || e.key === 'Enter') { this._confirm(); return true; }
      return true;                       // パネル表示中は移動させない
    }
    if (e.code === 'Space' || e.key === 'Enter') { this._interact(); return true; }
    if (this._dirOf(e.code) !== null) { this.keys.add(e.code); this.path = null; return true; }
    return false;
  }

  onKeyUp(e) {
    if (this._dirOf(e.code) !== null) { this.keys.delete(e.code); return true; }
    return false;
  }

  _dirOf(code) {
    switch (code) {
      case 'ArrowDown': case 'KeyS': return DOWN;
      case 'ArrowLeft': case 'KeyA': return LEFT;
      case 'ArrowRight': case 'KeyD': return RIGHT;
      case 'ArrowUp': case 'KeyW': return UP;
      default: return null;
    }
  }

  _onPointer(e) {
    if (this.talk) { this._advance(); return; }
    if (this.panelFor || this.shop) return;
    const r = this.canvas.getBoundingClientRect();
    const dpr = this.canvas.width / r.width;
    const wx = ((e.clientX - r.left) * dpr - this.offX) / this.scale;
    const wy = ((e.clientY - r.top) * dpr - this.offY) / this.scale;
    const c = Math.floor(wx / TILE), rr = Math.floor(wy / TILE);
    if (c < 0 || c >= this.cols || rr < 0 || rr >= this.rows) return;

    const ent = this._entityAt(c, rr);
    if (ent) {
      const spot = this._approachTile(c, rr);
      if (!spot) return;
      const [fc, fr, dc, dr] = spot;
      // 進む向きの逆がエンティティを向く方向
      const face = DIRS.findIndex(([x, y]) => x === -dc && y === -dr);
      const tile = this._tileOf(this.p);
      if (tile.c === fc && tile.r === fr) {    // すでに目の前
        this.p.dir = face;
        this._trigger(ent);
        return;
      }
      this._walkTo(fc, fr, { ent, face });
    } else if (!this._solid(c, rr)) {
      this._walkTo(c, rr, null);
    }
  }

  _tileOf(p) { return { c: Math.floor(p.x / TILE), r: Math.floor(p.y / TILE) }; }

  // ---------------------------------------------------------------- 経路

  _walkTo(tc, tr, goal) {
    const s = this._tileOf(this.p);
    const path = this._bfs(s.c, s.r, tc, tr);
    if (!path) return;
    this.path = path;
    this.pathGoal = goal;
    this.keys.clear();
  }

  _bfs(sc, sr, tc, tr) {
    if (this._solid(tc, tr)) return null;
    const key = (c, r) => r * this.cols + c;
    const prev = new Map();
    const q = [[sc, sr]];
    const seen = new Set([key(sc, sr)]);
    while (q.length) {
      const [c, r] = q.shift();
      if (c === tc && r === tr) {
        const out = [];
        let cur = key(c, r), cc = c, rr = r;
        while (cur !== key(sc, sr)) {
          out.unshift([cc, rr]);
          const p = prev.get(cur);
          if (!p) return null;
          [cc, rr] = p;
          cur = key(cc, rr);
        }
        return out;
      }
      for (const [dc, dr] of DIRS) {
        const nc = c + dc, nr = r + dr, k = key(nc, nr);
        if (seen.has(k) || this._solid(nc, nr)) continue;
        seen.add(k);
        prev.set(k, [c, r]);
        q.push([nc, nr]);
      }
    }
    return null;
  }

  // ---------------------------------------------------------------- 更新

  update() {
    if (!this.visible) return;
    this.frame++;
    // 案内係は会話やパネルが開いていても歩き続ける（先に行って待つため）
    this._updateGuide();
    if (this.panelFor || this.talk || this.shop) { this.p.moving = false; return; }

    let vx = 0, vy = 0;

    if (this.path && this.path.length) {
      // 次のマスの中心へ向かう
      const [tc, tr] = this.path[0];
      const cx = tc * TILE + TILE / 2, cy = tr * TILE + TILE / 2;
      const dx = cx - this.p.x, dy = cy - this.p.y;
      const d = Math.hypot(dx, dy);
      if (d < SPEED) {
        this.p.x = cx; this.p.y = cy;
        this.path.shift();
        if (!this.path.length) {
          this.path = null;
          const goal = this.pathGoal;
          this.pathGoal = null;
          if (goal) {
            this.p.dir = goal.face;
            this._trigger(goal.ent);
          }
        }
      } else {
        vx = (dx / d) * SPEED; vy = (dy / d) * SPEED;
      }
    } else {
      for (const code of this.keys) {
        const d = this._dirOf(code);
        if (d === DOWN) vy += 1;
        else if (d === UP) vy -= 1;
        else if (d === LEFT) vx -= 1;
        else if (d === RIGHT) vx += 1;
      }
      const len = Math.hypot(vx, vy);
      if (len > 0) { vx = (vx / len) * SPEED; vy = (vy / len) * SPEED; }
    }

    if (vx || vy) {
      // 向きは移動量の大きい軸で決める
      if (Math.abs(vx) > Math.abs(vy)) this.p.dir = vx > 0 ? RIGHT : LEFT;
      else this.p.dir = vy > 0 ? DOWN : UP;
      this._move(vx, vy);
      this.p.moving = true;
      this.p.anim += Math.hypot(vx, vy);
      if (this.audio && Math.floor(this.p.anim / 13) !== this._lastStep) {
        this._lastStep = Math.floor(this.p.anim / 13);
        this.audio.step?.();
      }
    } else {
      this.p.moving = false;
    }
  }

  /**
   * 案内係を歩かせる。着いたら onArrive を呼ぶ。
   * 玉の当たり判定とは関係ないので、通れる床だけを辿れば十分。
   */
  walkGuideTo(tc, tr, onArrive = null) {
    if (!this.guide) return false;
    const g = this._tileOf(this.guide);
    const path = this._bfs(g.c, g.r, tc, tr);
    if (!path) return false;
    this.guide.path = path;
    this.guide.onArrive = onArrive;
    return true;
  }

  /** プレイヤーを歩かせる（案内が「ついてきて」と言うときに使う） */
  walkPlayerTo(tc, tr) { this._walkTo(tc, tr, null); }

  /** 案内係を持ち場へ戻す */
  guideGoHome() {
    if (this.guide) this.walkGuideTo(this.guide.home[0], this.guide.home[1]);
  }

  _updateGuide() {
    const g = this.guide;
    if (!g || !g.path || !g.path.length) { if (g) g.moving = false; return; }
    const [tc, tr] = g.path[0];
    const cx = tc * TILE + TILE / 2, cy = tr * TILE + TILE / 2;
    const dx = cx - g.x, dy = cy - g.y;
    const d = Math.hypot(dx, dy);
    if (d < SPEED) {
      g.x = cx; g.y = cy;
      g.path.shift();
      if (!g.path.length) {
        g.path = null;
        g.moving = false;
        const done = g.onArrive;
        g.onArrive = null;
        done?.();
      }
      return;
    }
    const vx = (dx / d) * SPEED, vy = (dy / d) * SPEED;
    if (Math.abs(vx) > Math.abs(vy)) g.dir = vx > 0 ? RIGHT : LEFT;
    else g.dir = vy > 0 ? DOWN : UP;
    g.x += vx; g.y += vy;
    g.moving = true;
    g.anim += SPEED;
  }

  // 軸ごとに分けて押し戻す（壁に沿って滑るようにするため）
  _move(vx, vy) {
    const p = this.p;
    p.x += vx;
    if (this._hits(p.x, p.y)) { p.x -= vx; this._slide(p, 'x', vx); }
    p.y += vy;
    if (this._hits(p.x, p.y)) { p.y -= vy; this._slide(p, 'y', vy); }
    p.x = Math.max(PLAYER_R, Math.min(this.mapW - PLAYER_R, p.x));
    p.y = Math.max(PLAYER_R, Math.min(this.mapH - PLAYER_R, p.y));
  }

  _slide(p, axis, v) {
    // 角に軽く引っかかったときだけ、少しずらして通す
    for (const s of [1.4, -1.4, 2.6, -2.6]) {
      if (axis === 'x') {
        if (!this._hits(p.x + v, p.y + s)) { p.x += v; p.y += s; return; }
      } else if (!this._hits(p.x + s, p.y + v)) { p.x += s; p.y += v; return; }
    }
  }

  _hits(x, y) {
    const r = PLAYER_R - 1.5;
    for (const [ox, oy] of [[-r, -r], [r, -r], [-r, r], [r, r]]) {
      if (this._solid(Math.floor((x + ox) / TILE), Math.floor((y + oy) / TILE))) return true;
    }
    return false;
  }

  // ---------------------------------------------------------------- 対象

  _machineAt(c, r) {
    const ch = this._at(c, r);
    const i = (this.area.machines || '').indexOf(ch);
    return i >= 0 ? MACHINES[i] : null;
  }

  _slotAt(c, r) {
    const ch = this._at(c, r);
    const i = (this.area.customs || '').indexOf(ch);
    return i >= 0 ? i : -1;
  }

  _npcAt(c, r) {
    const i = (this.area.npcs || '').indexOf(this._at(c, r));
    return i >= 0 ? NPCS[i] : null;
  }

  _entityAt(c, r) {
    // 案内係は動くのでマップには居ない。今いるマスで見る
    if (this.guide) {
      const g = this._tileOf(this.guide);
      if (g.c === c && g.r === r) return { kind: 'guide', guide: this.guide, c, r };
    }
    const ch = this._at(c, r);
    const m = this._machineAt(c, r);
    if (m) return { kind: 'machine', machine: m, c, r };
    const slot = this._slotAt(c, r);
    if (slot >= 0) return { kind: 'custom', slot, machine: this.customs[slot] || null, c, r };
    const n = this._npcAt(c, r);
    if (n) return { kind: 'npc', npc: n, c, r };
    const spot = this.area.spots?.[ch];
    if (spot) return { kind: 'spot', spot, c, r };
    const door = this.area.doors?.[ch];
    if (door) return { kind: 'door', door, c, r };
    return null;
  }

  /** 今向いている先にあるもの */
  facingEntity() {
    const tile = this._tileOf(this.p);
    const [dc, dr] = DIRS[this.p.dir];
    return this._entityAt(tile.c + dc, tile.r + dr);
  }

  _interact() { this._trigger(this.facingEntity()); }

  // エンティティに接する空きマスのうち、いま近いものを選ぶ
  _approachTile(c, r) {
    let best = null, bestD = Infinity;
    const tile = this._tileOf(this.p);
    // 台は正面（下）から。それ以外は四方どこからでも
    const order = this._isMachineTile(c, r) ? [[0, 1]] : DIRS;
    for (const [dc, dr] of order) {
      const nc = c + dc, nr = r + dr;
      if (this._solid(nc, nr)) continue;
      const d = Math.abs(nc - tile.c) + Math.abs(nr - tile.r);
      if (d < bestD) { bestD = d; best = [nc, nr, dc, dr]; }
    }
    return best;
  }

  _trigger(ent) {
    if (!ent) return;
    if (ent.kind === 'guide') { this.on.onGuide?.(); return; }
    if (ent.kind === 'machine') this._openPanel(ent.machine);
    else if (ent.kind === 'custom') this._openCustomPanel(ent);
    else if (ent.kind === 'npc') {
      this._openDialogue(npcName(ent.npc), ent.npc.color,
        ent.npc.talk({ dateKey: todayKey(), rng: Math.random }));
    }
    else if (ent.kind === 'door') this._useDoor(ent.door);
    else if (ent.kind === 'spot') {
      const s = ent.spot;
      if (s.kind === 'talk') {
        this._openDialogue(t(`spot.${s.key}.name`), s.color, tList(`spot.${s.key}.lines`));
      } else {
        this._openShop(s.kind, t(`spot.${s.key}.name`));
      }
    }
  }

  _useDoor(door) {
    this.audio?.select?.();
    this.goTo(door.to, door.spawn);
  }

  // ---------------------------------------------------------------- 会話

  /**
   * @param {Array} [choices] 最後の行で選ばせる {label, value} の並び。
   *   選ぶと onChoice(value) が呼ばれる（選ばずに閉じたら null）
   */
  _openDialogue(name, color, lines, choices = null, onChoice = null) {
    this.talk = { name, lines, i: 0, choices, onChoice, pick: 0 };
    this.keys.clear();
    this.audio?.select?.();
    this.talkBox.style.setProperty('--n', color);
    this._showLine();
    this.talkBox.classList.remove('gone');
  }

  _showLine() {
    const d = this.talk;
    this.talkName.textContent = d.name;
    this.talkLine.textContent = d.lines[d.i];
    const last = d.i >= d.lines.length - 1;
    const asking = last && d.choices;
    this.talkBox.querySelector('.talk-next').style.visibility =
      !last && !asking ? 'visible' : 'hidden';

    this.choiceBox.innerHTML = asking ? d.choices.map((c, i) =>
      `<button type="button" data-choice="${i}" class="${i === d.pick ? 'on' : ''}">${esc(c.label)}</button>`
    ).join('') : '';
    this.choiceBox.classList.toggle('gone', !asking);
    if (!asking) return;
    for (const b of this.choiceBox.querySelectorAll('[data-choice]')) {
      b.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        this.talk.pick = Number(b.dataset.choice);
        this._choose();
      });
    }
  }

  /** 選択肢を左右に動かす */
  _movePick(d) {
    const t2 = this.talk;
    if (!t2?.choices) return false;
    if (t2.i < t2.lines.length - 1) return false;
    t2.pick = (t2.pick + d + t2.choices.length) % t2.choices.length;
    this.audio?.step?.();
    this._showLine();
    return true;
  }

  _choose() {
    const t2 = this.talk;
    if (!t2?.choices) return;
    const value = t2.choices[t2.pick]?.value;
    const cb = t2.onChoice;
    this.audio?.select?.();
    this._closeDialogue();
    cb?.(value);
  }

  _advance() {
    const d = this.talk;
    if (!d) return;
    // 最後の行に選択肢があるなら、そこで止めて選ばせる
    if (d.choices && d.i >= d.lines.length - 1) { this._choose(); return; }
    d.i++;
    if (d.i >= d.lines.length) this._closeDialogue();
    else { this.audio?.step?.(); this._showLine(); }
  }

  _closeDialogue() {
    this.talk = null;
    this.talkBox.classList.add('gone');
    this.choiceBox.classList.add('gone');
  }

  // ---------------------------------------------------------------- 台のパネル

  _openPanel(m) {
    if (!m) return;
    this._showPanel({ kind: 'machine', machine: m }, `
      ${this._specsHtml(m)}
      <div class="mpanel-btns">
        <button class="mpanel-cancel" type="button">${esc(t('panel.cancel'))}</button>
        <button class="mpanel-play" type="button">${esc(t('panel.play'))}</button>
      </div>`);
  }

  // 自作台。まだ何も作られていない枠なら、そのままエディタへの入口になる。
  _openCustomPanel(ent) {
    const m = ent.machine;
    if (!m) {
      this._showPanel({ kind: 'custom', slot: ent.slot, machine: null }, `
        <p class="machine-blurb">${esc(t('panel.emptyBlurb'))}</p>
        <p class="mpanel-rec">${esc(t('panel.emptyNote'))}</p>
        <div class="mpanel-btns">
          <button class="mpanel-cancel" type="button">${esc(t('panel.cancel'))}</button>
          <button class="mpanel-play" type="button">${esc(t('panel.create'))}</button>
        </div>`, {
        color: '#7f8aa8', accent: '#ffd166', icon: '＋',
        name: t('panel.emptyName', { n: ent.slot + 1 }), type: t('panel.emptyType'),
      });
      return;
    }
    this._showPanel({ kind: 'custom', slot: ent.slot, machine: m }, `
      ${this._specsHtml(m)}
      <div class="mpanel-btns three">
        <button class="mpanel-cancel" type="button">${esc(t('panel.cancel'))}</button>
        <button class="mpanel-edit" type="button">${esc(t('panel.edit'))}</button>
        <button class="mpanel-play" type="button">${esc(t('panel.play'))}</button>
      </div>
      <button class="mpanel-trash" type="button">${esc(t('panel.trash'))}</button>`);
  }

  _specsHtml(m) {
    const s = this.stats[m.id];
    const rec = s && s.spins
      ? t('panel.record', { spins: num(s.spins), jackpots: num(s.jackpots) })
      : t('panel.notPlayed');
    const nail = m.custom
      ? (m.diag ? t('panel.nailDiag', { n: m.diag.startPct.toFixed(1) }) : t('panel.notMeasured'))
      : nailLabel(m.nail);
    return `
      <dl class="machine-specs">
        <dt>${esc(t('panel.odds'))}</dt><dd>${esc(t('panel.oddsValue', { n: m.odds }))}</dd>
        <dt>${esc(t('panel.rounds'))}</dt><dd>${esc(t('panel.roundsValue', { n: m.rounds }))}</dd>
        <dt>${esc(t('panel.payout'))}</dt><dd>${esc(t('panel.payoutValue', { n: num(estimatePayout(m)) }))}</dd>
        <dt>${esc(t('panel.nail'))}</dt><dd>${esc(nail)}</dd>
      </dl>
      <p class="machine-blurb">${esc(machineBlurb(m))}</p>
      <p class="mpanel-rec">${esc(rec)}${m.custom ? esc(t('panel.medalNote')) : ''}</p>`;
  }

  _showPanel(target, body, look = null) {
    this.panelFor = target;
    this.keys.clear();
    this.audio?.select?.();

    const m = look || target.machine;
    this.panel.style.setProperty('--m', m.color);
    this.panel.style.setProperty('--m-accent', m.accent);
    this.panel.innerHTML = `
      <div class="mpanel-card">
        <div class="mpanel-head">
          <span class="mpanel-icon">${esc(m.icon)}</span>
          <div>
            <strong class="mpanel-name">${esc(look ? m.name : machineName(m))}</strong>
            <span class="mpanel-type">${esc(look ? m.type : machineType(m))}</span>
          </div>
        </div>
        ${body}
      </div>`;
    this.panel.classList.remove('gone');
    this.panel.querySelector('.mpanel-cancel').addEventListener('click', () => this._closePanel());
    this.panel.querySelector('.mpanel-play').addEventListener('click', () => this._confirm());
    this.panel.querySelector('.mpanel-edit')?.addEventListener('click', () => {
      const cur = this.panelFor;
      this._closePanel();
      this.on.onEdit?.(cur.machine, cur.slot);
    });
    this.panel.querySelector('.mpanel-trash')?.addEventListener('click', () => {
      const cur = this.panelFor;
      if (!confirm(t('panel.confirmTrash', { name: machineName(cur.machine) }))) return;
      this._closePanel();
      this.on.onDelete?.(cur.machine);
    });
  }

  _closePanel() {
    this.panelFor = null;
    this.panel?.classList.add('gone');
  }

  _confirm() {
    const cur = this.panelFor;
    this._closePanel();
    if (!cur) return;
    // 空き枠の「遊ぶ」はエディタを開くこと自体が目的
    if (cur.kind === 'custom' && !cur.machine) this.on.onEdit?.(null, cur.slot);
    else if (cur.machine) this.on.onPick?.(cur.machine);
  }

  // ---------------------------------------------------------------- 売店

  /** kind: prizes（景品カウンター） / exchange（換金所） / collection（自宅の棚） */
  _openShop(kind, name) {
    this.shop = { kind, name, tab: kind === 'prizes' ? 'trade' : null, msg: '' };
    this.keys.clear();
    this.audio?.select?.();
    this._renderShop();
    this.shopEl.classList.remove('gone');
  }

  _closeShop() {
    this.shop = null;
    this.shopEl?.classList.add('gone');
  }

  _trade(kind, payload) {
    const res = this.on.onTrade?.(kind, payload) || { ok: false, msg: t('trade.unavailable') };
    this.shop.msg = res.msg;
    if (res.ok) this.audio?.select?.(); else this.audio?.step?.();
    this._renderShop();
  }

  _renderShop() {
    const s = this.shop;
    if (!s) return;
    const body = s.kind === 'prizes' ? this._shopPrizes()
      : s.kind === 'exchange' ? this._shopExchange()
      : this._shopCollection();

    this.shopEl.innerHTML = `
      <div class="shop-card">
        <div class="shop-head">
          <strong>${esc(s.name)}</strong>
          <span class="shop-purse">
            <b>${num(this.wallets.ball)}</b>${esc(t('currency.ball.label'))}
            <b>${num(this.wallets.ing)}</b>${esc(t('currency.ing.label'))}
          </span>
          <button class="shop-close" type="button" aria-label="${esc(t('shop.close'))}">×</button>
        </div>
        ${s.kind === 'prizes' ? `
          <nav class="shop-tabs">
            <button type="button" data-tab="trade" class="${s.tab === 'trade' ? 'on' : ''}">${esc(t('shop.tab.trade'))}</button>
            <button type="button" data-tab="lend" class="${s.tab === 'lend' ? 'on' : ''}">${esc(t('shop.tab.lend'))}</button>
          </nav>` : ''}
        <div class="shop-body">${body}</div>
        <p class="shop-msg">${esc(s.msg)}</p>
      </div>`;

    this.shopEl.querySelector('.shop-close').addEventListener('click', () => this._closeShop());
    for (const b of this.shopEl.querySelectorAll('[data-tab]')) {
      b.addEventListener('click', () => { s.tab = b.dataset.tab; s.msg = ''; this._renderShop(); });
    }
    for (const b of this.shopEl.querySelectorAll('[data-buy]')) {
      b.addEventListener('click', () => this._trade('buy-prize', { prizeId: b.dataset.buy }));
    }
    for (const b of this.shopEl.querySelectorAll('[data-cash]')) {
      b.addEventListener('click', () =>
        this._trade('cash-in', { prizeId: b.dataset.cash, n: Number(b.dataset.n) }));
    }
    for (const b of this.shopEl.querySelectorAll('[data-lend]')) {
      b.addEventListener('click', () => this._trade('buy-balls', { ing: Number(b.dataset.lend) }));
    }
  }

  _shopPrizes() {
    if (this.shop.tab === 'lend') {
      const have = this.wallets.ing;
      const amounts = [25, 100, 500, have].filter((n, i, a) => n > 0 && a.indexOf(n) === i);
      return `
        <p class="shop-note">${esc(t('shop.lend.note', { n: BALLS_PER_ING }))}</p>
        <div class="lend-grid">
          ${amounts.length ? amounts.map((n) => `
            <button type="button" data-lend="${n}" ${n > have ? 'disabled' : ''}>
              <b>${num(n)}</b><span>${esc(t('currency.ing.label'))}</span>
              <em>${esc(t('unit.balls', { n: num(n * BALLS_PER_ING) }))}</em>
            </button>`).join('')
            : `<p class="shop-note">${esc(t('shop.lend.none'))}</p>`}
        </div>`;
    }

    return `
      <p class="shop-note">${esc(t('shop.trade.note'))}</p>
      ${PRIZE_GROUPS.map((g) => `
        <h4 class="shop-h">${esc(groupLabel(g))}</h4>
        <ul class="prize-list">
          ${PRIZES.filter((p) => p.group === g).map((p) => this._prizeRow(p)).join('')}
        </ul>`).join('')}`;
  }

  _prizeRow(p) {
    const own = this.collection[p.id] || 0;
    const can = this.wallets.ball >= p.cost;
    return `
      <li class="prize ${can ? '' : 'poor'}" style="--r:${RARITY[p.rarity].color}">
        <span class="prize-icon">${p.icon}</span>
        <div class="prize-main">
          <b>${esc(prizeName(p))}</b>
          <small>${esc(prizeBlurb(p))}</small>
        </div>
        <div class="prize-side">
          <span class="prize-cost">${esc(t('unit.balls', { n: num(p.cost) }))}</span>
          ${own ? `<span class="prize-own">${esc(t('shop.own', { n: num(own) }))}</span>` : ''}
        </div>
        <button type="button" data-buy="${p.id}" ${can ? '' : 'disabled'}>${esc(t('shop.buy'))}</button>
      </li>`;
  }

  _shopExchange() {
    const owned = PRIZES.filter((p) => (this.collection[p.id] || 0) > 0);
    if (!owned.length) {
      return `
        <p class="shop-note dim">${esc(t('shop.exchange.unseen'))}</p>
        <p class="shop-note">${esc(t('shop.exchange.none'))}</p>`;
    }
    return `
      <p class="shop-note dim">${esc(t('shop.exchange.unseen'))}</p>
      <p class="shop-note">${esc(t('shop.exchange.note'))}</p>
      <ul class="prize-list">
        ${owned.map((p) => {
          const n = this.collection[p.id];
          const pct = Math.round(rateOf(p) * 100);
          return `
          <li class="prize" style="--r:${RARITY[p.rarity].color}">
            <span class="prize-icon">${p.icon}</span>
            <div class="prize-main">
              <b>${esc(prizeName(p))}</b>
              <small>${esc(t('shop.exchange.rate', { pct, ing: num(p.ing) }))}</small>
            </div>
            <div class="prize-side"><span class="prize-own">${esc(t('shop.own', { n: num(n) }))}</span></div>
            <div class="prize-btns">
              <button type="button" data-cash="${p.id}" data-n="1">${esc(t('shop.one'))}</button>
              <button type="button" data-cash="${p.id}" data-n="${n}">${esc(t('shop.all'))}</button>
            </div>
          </li>`;
        }).join('')}
      </ul>
      <p class="shop-note dim">${esc(t('shop.exchange.footer'))}</p>`;
  }

  _shopCollection() {
    const got = collected(this.collection);
    return `
      <p class="shop-note">${esc(t('shop.coll.summary', {
        got, total: PRIZES.length, owned: num(totalOwned(this.collection)),
      }))}</p>
      <ul class="coll-grid">
        ${PRIZES.map((p) => {
          const n = this.collection[p.id] || 0;
          return `
          <li class="coll ${n ? '' : 'unknown'}" style="--r:${RARITY[p.rarity].color}">
            <span class="coll-icon">${n ? p.icon : '?'}</span>
            <b>${n ? esc(prizeName(p)) : esc(t('shop.coll.unknown'))}</b>
            <small>${esc(n ? t('unit.pieces', { n: num(n) }) : t('unit.balls', { n: num(p.cost) }))}</small>
          </li>`;
        }).join('')}
      </ul>`;
  }

  // ---------------------------------------------------------------- 描画

  render() {
    if (!this.visible) return;
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(this.scale, 0, 0, this.scale, this.offX, this.offY);

    this._drawFloor(ctx);

    // 場所の空気
    const amb = ctx.createRadialGradient(
      this.mapW / 2, this.mapH * 0.3, 20, this.mapW / 2, this.mapH * 0.4, this.mapH);
    amb.addColorStop(0, this.area.ambient);
    amb.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = amb;
    ctx.fillRect(0, 0, this.mapW, this.mapH);

    // 手前のものが後に描かれるよう y で並べる
    const items = [];
    const A = this.area;
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const ch = this._at(c, r);
        const y = r * TILE;
        if ((A.machines || '').includes(ch)) {
          items.push({ y, draw: () => this._drawMachine(ctx, c, r, this._machineAt(c, r)) });
        } else if ((A.customs || '').includes(ch)) {
          const slot = this._slotAt(c, r);
          items.push({ y, draw: () => this._drawMachine(ctx, c, r, this.customs[slot] || null, slot) });
        } else if ((A.npcs || '').includes(ch)) {
          const npc = NPCS[A.npcs.indexOf(ch)];
          items.push({ y, draw: () => this._drawNpc(ctx, c, r, npc.body, npc.color) });
        } else if (A.spots?.[ch]) {
          items.push({ y, draw: () => this._drawSpot(ctx, c, r, A.spots[ch]) });
        } else if (A.doors?.[ch]) {
          items.push({ y, draw: () => this._drawDoor(ctx, c, r, A.doors[ch]) });
        } else if (A.scenery?.[ch]) {
          items.push({ y, draw: () => A.scenery[ch](ctx, c * TILE, r * TILE, this.frame) });
        }
      }
    }
    items.push({ y: this.p.y, draw: () => this._drawPlayer(ctx) });
    if (this.guide) items.push({ y: this.guide.y, draw: () => this._drawGuide(ctx) });
    items.sort((a, b) => a.y - b.y);
    for (const it of items) it.draw();

    this._drawPrompt(ctx);
  }

  _drawFloor(ctx) {
    const pal = this.area.palette;
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const ch = this._at(c, r);
        const x = c * TILE, y = r * TILE;
        if (ch === '#') continue;
        ctx.fillStyle = (c + r) % 2 === 0 ? pal.floor[0] : pal.floor[1];
        ctx.fillRect(x, y, TILE, TILE);
      }
    }
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (this._at(c, r) !== '#') continue;
        const x = c * TILE, y = r * TILE;
        ctx.fillStyle = pal.wall;
        ctx.fillRect(x, y, TILE, TILE);
        // 床に面した側だけ縁を入れる
        if (this._at(c, r + 1) !== '#') {
          ctx.fillStyle = pal.edge;
          ctx.fillRect(x, y + TILE - 3, TILE, 3);
        }
      }
    }
  }

  _drawMachine(ctx, c, r, m, slot = -1) {
    const x = c * TILE, y = r * TILE;
    const f = this.frame;
    // 台は 1 マスより背を高く描いて、島に食い込ませる（奥行きを出す）
    const top = y - 12;

    // まだ作られていない自作台の枠。ここだけは暗いままにして、空いていることを見せる
    if (!m) {
      ctx.fillStyle = '#150710';
      ctx.fillRect(x + 1, top, TILE - 2, TILE + 12);
      ctx.strokeStyle = 'rgba(160,180,220,0.35)';
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      ctx.strokeRect(x + 1.5, top + 0.5, TILE - 3, TILE + 11);
      ctx.setLineDash([]);
      ctx.fillStyle = `rgba(255,209,102,${0.45 + 0.35 * Math.sin(f * 0.07 + c)})`;
      ctx.font = '700 12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('＋', x + TILE / 2, top + 13);
      ctx.fillStyle = 'rgba(200,220,255,0.4)';
      ctx.font = '600 6px system-ui, sans-serif';
      ctx.fillText(t('panel.emptyShort', { n: slot + 1 }), x + TILE / 2, y + TILE - 4);
      return;
    }

    ctx.fillStyle = '#1c0912';
    ctx.fillRect(x + 1, top, TILE - 2, TILE + 12);
    ctx.strokeStyle = m.color;
    ctx.globalAlpha = 0.75;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 1.5, top + 0.5, TILE - 3, TILE + 11);
    ctx.globalAlpha = 1;

    // 上部ランプ
    const pulse = 0.55 + 0.45 * Math.sin(f * 0.09 + c);
    ctx.fillStyle = m.accent;
    ctx.globalAlpha = pulse;
    ctx.fillRect(x + 3, top + 2, TILE - 6, 2.5);
    ctx.globalAlpha = 1;

    // 液晶
    ctx.fillStyle = '#05070f';
    ctx.fillRect(x + 3, top + 6, TILE - 6, 11);
    ctx.fillStyle = m.color;
    ctx.font = '700 8px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = 0.5 + 0.5 * pulse;
    ctx.fillText(m.icon + m.icon + m.icon, x + TILE / 2, top + 11.5);
    ctx.globalAlpha = 1;

    // 受け皿
    ctx.fillStyle = '#43121f';
    ctx.fillRect(x + 3, top + 19, TILE - 6, 6);

    // 台番号
    ctx.fillStyle = 'rgba(200,220,255,0.55)';
    ctx.font = '600 6px system-ui, sans-serif';
    ctx.fillText(machineShort(m), x + TILE / 2, y + TILE - 4);
  }

  // 交換窓口・棚など、話しかけられる設備
  _drawSpot(ctx, c, r, spot) {
    const x = c * TILE, y = r * TILE;
    const f = this.frame;

    if (spot.shelf) {
      // 棚には持っている景品を実際に並べる。交換するほど賑やかになる
      spot.draw?.(ctx, x, y, f);
      const idx = this.shelfTiles.findIndex(([sc, sr]) => sc === c && sr === r);
      const owned = PRIZES.filter((p) => (this.collection[p.id] || 0) > 0);
      const perShelf = Math.max(1, Math.ceil(PRIZES.length / Math.max(1, this.shelfTiles.length)));
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.font = '9px system-ui, sans-serif';
      owned.slice(idx * perShelf, idx * perShelf + 3).forEach((p, i) => {
        ctx.fillText(p.icon, x + 2 + i * 8, y + TILE - 8);
      });
      return;
    }
    if (spot.draw) { spot.draw(ctx, x, y, f); return; }

    // その場に立っているだけの人
    if (spot.kind === 'talk') {
      this._drawNpc(ctx, c, r, '#6b5a3f', spot.color);
      return;
    }

    // 景品カウンター。人をカウンターの向こう側に立たせたいので、先に人を
    // 描いてから天板をかぶせる。そうすると腰から下が隠れて奥行きが出る。
    if (spot.clerk) {
      ctx.fillStyle = '#5c1424';
      ctx.fillRect(x, y - 10, TILE, TILE + 10);
      this._drawPerson(ctx, x + TILE / 2, y + 2, DOWN, spot.clerk.body, '#ffd9b8', 0, false);
      ctx.fillStyle = '#7d1c2e';
      ctx.fillRect(x, y + TILE - 9, TILE, 9);
      ctx.fillStyle = 'rgba(255,209,102,0.85)';
      ctx.fillRect(x, y + TILE - 10, TILE, 1.5);
      ctx.fillStyle = spot.color;
      ctx.globalAlpha = 0.55 + 0.45 * Math.sin(f * 0.08 + c);
      ctx.fillRect(x + 2, y - 8, TILE - 4, 2.5);
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.font = '600 6px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(t(`spot.${spot.key}.sign`), x + TILE / 2, y + TILE - 4.5);
      return;
    }

    // 窓口。中の人は見えない。奥は暗いままで、時おり目だけが光る
    ctx.fillStyle = '#241c2c';
    ctx.fillRect(x, y - 6, TILE, TILE + 6);
    ctx.fillStyle = '#05070c';
    ctx.fillRect(x + 3, y + 4, TILE - 6, 11);
    if (spot.faceless) {
      // 十数秒に一度だけまばたきさせる。動くものが少ないほど気づく
      const blink = Math.sin(f * 0.013);
      if (blink > 0.985) {
        ctx.fillStyle = 'rgba(200,235,255,0.75)';
        ctx.fillRect(x + 8, y + 9, 2, 1.5);
        ctx.fillRect(x + TILE - 10, y + 9, 2, 1.5);
      }
    }
    const pulse = 0.5 + 0.5 * Math.sin(f * 0.08 + c);
    ctx.fillStyle = spot.color;
    ctx.globalAlpha = 0.4 + pulse * 0.5;
    ctx.fillRect(x + 2, y - 4, TILE - 4, 3);
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(255,255,255,0.78)';
    ctx.font = '700 6px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(spot.sign || t(`spot.${spot.key}.sign`), x + TILE / 2, y - 2.5);
    ctx.fillStyle = 'rgba(200,220,255,0.5)';
    ctx.font = '600 5.5px system-ui, sans-serif';
    ctx.fillText(t(`spot.${spot.key}.sign`), x + TILE / 2, y + TILE - 3);
  }

  _drawDoor(ctx, c, r, door) {
    const x = c * TILE, y = r * TILE;
    ctx.fillStyle = '#1a1526';
    ctx.fillRect(x, y, TILE, TILE);
    ctx.fillStyle = door.color;
    ctx.globalAlpha = 0.28 + 0.22 * Math.sin(this.frame * 0.07 + c);
    ctx.fillRect(x + 3, y + 4, TILE - 6, TILE - 8);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = door.color;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x + 3, y + 4, TILE - 6, TILE - 8);
    ctx.fillStyle = '#ffe9a8';
    ctx.beginPath();
    ctx.arc(x + TILE - 8, y + TILE / 2, 1.6, 0, Math.PI * 2);
    ctx.fill();
  }

  _drawNpc(ctx, c, r, body, color) {
    const x = c * TILE + TILE / 2;
    const y = r * TILE + TILE / 2 + Math.sin(this.frame * 0.05 + c) * 0.8;
    this._drawPerson(ctx, x, y, DOWN, body, '#ffd9b8', 0, false);
    // 話しかけられることを示す小さな印
    const blink = this.frame * 0.08 + c;
    ctx.globalAlpha = 0.5 + 0.5 * Math.sin(blink);
    ctx.fillStyle = color;
    ctx.font = '700 9px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText('!', x, y - 16);
    ctx.globalAlpha = 1;
  }

  // 案内係。他の NPC と同じ姿だが、動くので位置は自前で持っている
  _drawGuide(ctx) {
    const g = this.guide;
    const bob = g.moving ? Math.sin(g.anim * 0.35) * 1.2 : Math.sin(this.frame * 0.05) * 0.8;
    this._drawPerson(ctx, g.x, g.y + bob, g.dir, g.body, '#ffd9b8',
      g.moving ? Math.sin(g.anim * 0.35) * 3 : 0, true);
    if (g.moving) return;
    const t2 = this.frame * 0.08;
    ctx.globalAlpha = 0.5 + 0.5 * Math.sin(t2);
    ctx.fillStyle = g.color;
    ctx.font = '700 10px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText('?', g.x, g.y - 16);
    ctx.globalAlpha = 1;
  }

  _drawPlayer(ctx) {
    const p = this.p;
    const bob = p.moving ? Math.sin(p.anim * 0.35) * 1.2 : 0;
    this._drawPerson(ctx, p.x, p.y + bob, p.dir, '#4a7dff', '#ffd9b8',
      p.moving ? Math.sin(p.anim * 0.35) * 3 : 0, true);
  }

  // プレイヤーと NPC で共通の人物描画
  _drawPerson(ctx, x, y, dir, cloth, skin, swing, shadow) {
    if (shadow) {
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      ctx.ellipse(x, y + 8, 7, 3.2, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // 足
    ctx.fillStyle = '#26304f';
    ctx.fillRect(x - 5, y + 3 + Math.max(0, swing), 4, 5);
    ctx.fillRect(x + 1, y + 3 + Math.max(0, -swing), 4, 5);

    // 胴
    ctx.fillStyle = cloth;
    roundRect(ctx, x - 6, y - 4, 12, 10, 3);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    roundRect(ctx, x - 6, y - 4, 12, 4, 3);
    ctx.fill();

    // 頭
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.arc(x, y - 9, 6, 0, Math.PI * 2);
    ctx.fill();

    // 髪（向きで形を変える）
    ctx.fillStyle = '#2b2340';
    ctx.beginPath();
    if (dir === UP) {
      ctx.arc(x, y - 9, 6, 0, Math.PI * 2);
    } else {
      ctx.arc(x, y - 10.5, 6, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.rect(x - 6, y - 11, 12, 3);
    }
    ctx.fill();

    // 目
    if (dir !== UP) {
      ctx.fillStyle = '#241c33';
      const eyes = dir === LEFT ? [-3.4, -1.2] : dir === RIGHT ? [1.2, 3.4] : [-2.6, 2.6];
      for (const ex of eyes) {
        ctx.beginPath();
        ctx.arc(x + ex, y - 8, 1.1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // 目の前に何かあるときの案内
  _drawPrompt(ctx) {
    if (this.panelFor || this.talk || this.shop) return;
    const tile = this._tileOf(this.p);
    const [dc, dr] = DIRS[this.p.dir];
    const e = this._entityAt(tile.c + dc, tile.r + dr);
    if (!e) return;

    const color = e.kind === 'npc' ? e.npc.color
      : e.kind === 'guide' ? e.guide.color
      : e.kind === 'spot' ? e.spot.color
      : e.kind === 'door' ? e.door.color
      : (e.machine?.accent || '#ffd166');
    const label = e.kind === 'npc' || e.kind === 'guide' ? t('prompt.talk')
      : e.kind === 'spot' ? t(`spot.${e.spot.key}.label`)
      : e.kind === 'door' ? t(`door.${e.door.key}.label`)
      : e.machine ? t('prompt.inspect') : t('prompt.create');

    const x = (tile.c + dc) * TILE + TILE / 2;
    const y = (tile.r + dr) * TILE - 16 + Math.sin(this.frame * 0.14) * 2;

    // 下を向いているときは案内の真下に自分が立っているので、矢印を出すと
    // 頭に刺さって読めなくなる。そのときは札だけにする。
    if (dr <= 0) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(x, y + 7);
      ctx.lineTo(x - 5, y);
      ctx.lineTo(x + 5, y);
      ctx.closePath();
      ctx.fill();
    }

    ctx.font = '700 7px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const w = ctx.measureText(label).width + 8;
    ctx.fillStyle = 'rgba(6,8,18,0.78)';
    roundRect(ctx, x - w / 2, y - 11, w, 11, 3);
    ctx.fill();
    ctx.fillStyle = color;
    ctx.fillText(label, x, y - 5.5);
  }

  // ---------------------------------------------------------------- 外部API

  /**
   * @param {object} wallets    通貨ごとの残り {ball, medal, ing}
   * @param {object} stats      機種ごとの成績
   * @param {Array}  customs    自作台（slot 番目に入れておく）
   * @param {object} collection 景品の所持数
   */
  setState(wallets, stats, customs, collection) {
    this.wallets = { ...this.wallets, ...wallets };
    this.stats = stats || {};
    if (customs) this.customs = customs;
    if (collection) this.collection = collection;
    for (const [id, el] of Object.entries(this.walletEls)) {
      el.textContent = (this.wallets[id] || 0).toLocaleString();
    }
    if (this.shop) this._renderShop();
  }

  show() {
    this.visible = true;
    this.root.classList.remove('gone');
    this._closePanel();
    this._closeShop();
    this._closeDialogue();
    this.keys.clear();
    this._resize();
  }

  hide() {
    this.visible = false;
    this.root.classList.add('gone');
    this.keys.clear();
  }

  /** 台を選ぶ場所にいるか。エディタや試し打ちから戻るときに使う */
  get inHall() { return this.area?.id === 'hall'; }

  /** 外から喋らせる（案内係の台詞は tutorial.js が持っている） */
  say(name, color, lines, choices = null, onChoice = null) {
    this._openDialogue(name, color, lines, choices, onChoice);
  }

  /** 台の情報パネルを開いているか。案内が次へ進む合図に使う */
  get panelMachine() { return this.panelFor?.machine || null; }
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function esc(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
